// app/javascript/controllers/item_editor_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "item",
        "editModeBtn",
        "editModeBtnPlus",
        "editImageUpload",
        "categoryScroll"
    ]
    static classes = ["editing"]

    connect() {
        this.editing = false
        this.dragBound = false
        this.draggedItem = null

        if (localStorage.getItem("item_editor_edit_mode") === "true") {
            localStorage.removeItem("item_editor_edit_mode")
            this.editing = true
            this.setEdit(true)
            this.editModeBtnPlusTargets.forEach(btn => {
                btn.classList.add("hidden")
            })
            this.editImageUploadTargets.forEach(btn => {
                btn.classList.remove("hidden")
            })
        }
    }

    // 편집 모드 토글
    toggleEdit() {
        this.editing = !this.editing
        if (this.editModeBtnTarget && this.editingClasses?.length) {
            const span = this.editModeBtnTarget.querySelector("span")
            if (span) {
                span.textContent = this.editing ? "편집모드 종료" : "편집모드"
            }
            this.editModeBtnTarget.classList.toggle(this.editingClasses[0], this.editing)
        }
        this.editModeBtnPlusTargets.forEach(btn => {
            btn.classList.toggle("hidden", this.editing)
        })
        this.editImageUploadTargets.forEach(upload => {
            upload.classList.toggle("hidden", !this.editing)
        })
        this.setEdit(this.editing)
    }

    // 편집 모드 UI/드래그 상태 전환
    setEdit(isEditing) {
        if (isEditing) {
            this.enableDragDrop()
            this.showDeleteBtnAll()
            this.showItemFormAll()
        } else {
            this.disableDragDrop()
            this.hideDeleteBtnAll()
            this.hideItemFormAll()
        }
    }

    // 삭제 후 프레임 갱신 감지
    handleItemDeletion(event) {
        const turboFrame = event.target.closest("turbo-frame")
        if (!turboFrame) return

        const observer = new MutationObserver((mutations, obs) => {
            for (let mutation of mutations) {
                if (mutation.type === "childList") {
                    obs.disconnect()
                    const updatedFrame = document.getElementById(turboFrame.id)
                    if (!updatedFrame) return
                    const itemContainer = updatedFrame.querySelector(".item-container")
                    if (!itemContainer) return
                    const itemForm = itemContainer.querySelector(".item-form")
                    if (itemForm) itemForm.classList.remove("hidden")
                }
            }
        })

        observer.observe(turboFrame.parentElement, { childList: true, subtree: true })
        localStorage.setItem("item_editor_edit_mode", "true")
        window.location.reload()
    }

    // 수정 후 상태 갱신
    updateItem(event) {
        event.preventDefault()
        const frame = event.target.closest("turbo-frame")
        if (!frame) return

        setTimeout(() => {
            const itemContainer = frame.querySelector(".item-container")
            if (itemContainer) {
                this.showDeleteBtn(itemContainer)
                itemContainer.draggable = true
                itemContainer.classList.add("cursor-move")
            }
        }, 50)

        localStorage.setItem("item_editor_edit_mode", "true")

        const activeTab = document.querySelector('[data-controller~="tabs"] [data-tabs-target="tab"]:not([hidden])')
        if (activeTab) localStorage.setItem("tabs:last", activeTab.id)
        window.location.reload()
    }

    // ----- 카테고리 스크롤 -----
    scrollCategoryLeft() {
        if (this.hasCategoryScrollTarget) {
            this.#smoothScroll(this.categoryScrollTarget, -150)
        }
    }

    scrollCategoryRight() {
        if (this.hasCategoryScrollTarget) {
            this.#smoothScroll(this.categoryScrollTarget, 150)
        }
    }

    #smoothScroll(el, delta) {
        const start = el.scrollLeft
        const target = Math.max(0, start + delta)
        const duration = 200
        const startTime = performance.now()

        const step = (now) => {
            const elapsed = now - startTime
            const progress = Math.min(elapsed / duration, 1)
            const ease = progress < 0.5 ? 2 * progress * progress : -1 + (4 - 2 * progress) * progress
            el.scrollLeft = start + (target - start) * ease
            if (progress < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
    }

    // ----- UI 토글 유틸 -----
    showDeleteBtnAll() { this.toggleClassAll(".item-edit", true) }
    hideDeleteBtnAll() { this.toggleClassAll(".item-edit", false) }
    showItemFormAll()  { this.toggleClassAll(".item-form", true) }
    hideItemFormAll()  { this.toggleClassAll(".item-form", false) }

    showDeleteBtn(target) { this.toggleClass(target, ".item-edit", true) }
    hideDeleteBtn(target) { this.toggleClass(target, ".item-edit", false) }
    showItemForm(target)  { this.toggleClass(target, ".item-form", true) }
    hideItemForm(target)  { this.toggleClass(target, ".item-form", false) }

    toggleClassAll(selector, show) {
        this.itemTargets.forEach(item => this.toggleClass(item, selector, show))
    }
    toggleClass(container, selector, show) {
        const el = container.querySelector(selector)
        if (!el) return
        el.classList.toggle("hidden", !show)
    }

    // ----- Drag & Drop -----
    enableDragDrop() {
        this.itemTargets.forEach(item => {
            item.draggable = true
            item.classList.add("cursor-move")
        })
        this.bindDragEvents()
    }

    disableDragDrop() {
        this.itemTargets.forEach(item => {
            item.draggable = false
            item.classList.remove("cursor-move")
        })
        this.unbindDragEvents()
    }

    bindDragEvents() {
        if (this.dragBound) return
        this.dragStartHandler = this.handleDragStart.bind(this)
        this.dragOverHandler  = this.handleDragOver.bind(this)
        this.dropHandler      = this.handleDrop.bind(this)
        this.dragEndHandler   = this.handleDragEnd.bind(this)

        this.itemTargets.forEach(item => {
            item.addEventListener("dragstart", this.dragStartHandler)
            item.addEventListener("dragover",  this.dragOverHandler)
            item.addEventListener("drop",      this.dropHandler)
            item.addEventListener("dragend",   this.dragEndHandler)
        })
        this.dragBound = true
    }

    unbindDragEvents() {
        if (!this.dragBound) return
        this.itemTargets.forEach(item => {
            item.removeEventListener("dragstart", this.dragStartHandler)
            item.removeEventListener("dragover",  this.dragOverHandler)
            item.removeEventListener("drop",      this.dropHandler)
            item.removeEventListener("dragend",   this.dragEndHandler)
        })
        this.dragBound = false
    }

    handleDragStart(e) {
        const frame = e.target.closest("turbo-frame")
        if (!frame) return
        this.draggedItem = frame.querySelector(".item-container")
        if (!this.draggedItem) return
        this.draggedItem.classList.add("opacity-25")
        e.dataTransfer.effectAllowed = "move"
    }

    handleDragOver(e) {
        e.preventDefault()
        if (!this.draggedItem) return

        const frame = e.target.closest("turbo-frame")
        if (!frame) return

        const target = frame.querySelector(".item-container")
        if (!target || target === this.draggedItem) return

        // 같은 카테고리 컨테이너
        const catId = this.draggedItem.dataset.categoryId
        const categoryContent = document.querySelector(`[data-item-editor-category-id="${catId}"]`)
        if (!categoryContent) return

        // 같은 카테고리의 모든 카드에서 기존 하이라이트 제거
        categoryContent.querySelectorAll("[data-category-id]").forEach(el => {
            // 드래그 중인 카드 자체의 25% 투명도는 유지
            if (el !== this.draggedItem) {
                el.classList.remove("opacity-25")
            }
        })

        // 현재 hover 중인 타겟만 흐리게 표시
        target.classList.add("opacity-25")
        e.dataTransfer.dropEffect = "move"
    }

    handleDragEnd() {
        if (!this.draggedItem) return

        const catId = this.draggedItem.dataset.categoryId
        const categoryContent = document.querySelector(`[data-item-editor-category-id="${catId}"]`)
        if (categoryContent) {
            // 드래그가 끝나면 모든 강조 제거
            categoryContent.querySelectorAll("[data-category-id]").forEach(el => {
                el.classList.remove("opacity-25")
            })
        }

        this.draggedItem = null
    }

    async handleDrop(e) {
        e.preventDefault()
        if (!this.draggedItem) return

        const targetFrame = e.target.closest("turbo-frame")
        const sourceFrame = this.draggedItem.closest("turbo-frame")
        if (!targetFrame || !sourceFrame || targetFrame === sourceFrame) return

        const targetItem = targetFrame.querySelector(".item-container")
        const sourceItem = sourceFrame.querySelector(".item-container")
        if (!targetItem || !sourceItem) return

        // 같은 카테고리만 허용
        if (sourceItem.dataset.categoryId !== targetItem.dataset.categoryId) return

        const sPos = parseInt(sourceItem.dataset.position, 10)
        const tPos = parseInt(targetItem.dataset.position, 10)
        if (Number.isNaN(sPos) || Number.isNaN(tPos) || sPos === tPos) return

        // 1) DOM 스왑 (안전한 2-placeholder)
        this.swapFramesInDOM(sourceFrame, targetFrame)

        // 2) position 데이터도 교환
        const tmp = sourceItem.dataset.position
        sourceItem.dataset.position = targetItem.dataset.position
        targetItem.dataset.position = tmp

        // 3) 서버에 저장 (낙관적 갱신)
        try {
            await fetch("/pos/swap_items", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
                },
                body: JSON.stringify({
                    category_id: targetItem.dataset.categoryId,
                    source_position: sPos,
                    target_position: tPos
                })
            })
        } catch (err) {
            console.error(err)
        }

        this.draggedItem = null
    }

    // turbo-frame 두 개를 정확히 자리 교환 (2-placeholder 방식)
    swapFramesInDOM(sourceFrame, targetFrame) {
        const parent = sourceFrame.parentNode
        if (!parent || targetFrame.parentNode !== parent) return

        const sPh = document.createElement("div")
        const tPh = document.createElement("div")

        // 각 자리 기억
        parent.replaceChild(sPh, sourceFrame)
        parent.replaceChild(tPh, targetFrame)

        // 서로의 위치로 교차 삽입
        parent.replaceChild(sourceFrame, tPh)
        parent.replaceChild(targetFrame, sPh)
    }
}
